import type { ApplicationContext } from '@hc-frontend/core-context';
import type {
  HealthcareSessionId,
  HealthcareUserId,
} from '@hc-frontend/core-types';
import type { IErrorOperationResult } from '@hc-frontend/core-utils-business-operation';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';

import { LeadIdentifier, PartialSurveyAnswers } from '../../entities';
import type {
  GetAnswersRepository,
  GetAnswersResult,
} from '../../repositories';

export type GetAnswersUseCase = (
  sessionId: HealthcareSessionId,
  userId: HealthcareUserId,
) => GetAnswersResult;

export type GetAnswersFactoryParameters = GetAnswersRepository &
  Pick<ApplicationContext, 'log'>;

const logOperation = (
  log: ApplicationContext['log'],
  result: IErrorOperationResult<string>,
) => {
  log({
    context: 'server',
    type: 'error',
    message: result.errors[0].code,
    data: {
      rootCause: result.errors[0].rootCause,
    },
  });
};

export function getAnswersFactory({
  getAnswers,
  log,
}: GetAnswersFactoryParameters): GetAnswersUseCase {
  return async (sessionId, userId) => {
    const identifier = LeadIdentifier.safeParse({
      sessionId,
      userId,
    });

    if (!identifier.success) {
      const error = createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetAnswers',
            rootCause: identifier.error,
          },
        ],
      });

      logOperation(log, error);

      return error;
    }

    const answersResponse = await getAnswers({
      sessionId,
      userId,
    });

    if (!answersResponse.success) {
      logOperation(log, answersResponse);
      return answersResponse;
    }

    const prospectSession = PartialSurveyAnswers.safeParse(
      answersResponse.data,
    );

    if (!prospectSession.success) {
      const error = createErrorOperationResult({
        errors: [
          {
            code: 'failedToGetAnswers',
            rootCause: prospectSession.error,
          },
        ],
      });

      logOperation(log, error);

      return error;
    }

    return createSuccessOperationResult(prospectSession.data);
  };
}
